import { useEffect, useMemo, useState } from "react";
import { supabase, apiFetch } from "@/lib/supabase";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import {
  FileText, ClipboardCheck, Handshake, DollarSign, UserPlus, ClipboardList,
  Search, Plus, ArrowLeft, ExternalLink, Download, Printer, User,
} from "lucide-react";
import { PageHeader, StatusBadge, formatDate } from "@/lib/ui";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import { X } from "lucide-react";
import OnboardingPanel from "@/components/forms-workspace/OnboardingPanel";
import ServiceRequestPanel from "@/components/forms-workspace/ServiceRequestPanel";
import ContractPanel from "@/components/forms-workspace/ContractPanel";
import InvoicePanel from "@/components/forms-workspace/InvoicePanel";
import FormPanel from "@/components/forms-workspace/FormPanel";
import DocumentPanel from "@/components/forms-workspace/DocumentPanel";

const TABS = [
  { id: "onboarding",      label: "Onboarding",       icon: UserPlus,       recordLabel: "Start Basic Client Intake" },
  { id: "service_request", label: "Service Requests", icon: ClipboardCheck, recordLabel: "New Service Request" },
  { id: "contract",        label: "Contracts",        icon: Handshake,      recordLabel: "New Contract" },
  { id: "invoice",         label: "Invoices",         icon: DollarSign,     recordLabel: "New Invoice" },
  { id: "form",            label: "Forms",            icon: ClipboardList,  recordLabel: "Create New Form" },
  { id: "document",        label: "Documents",        icon: FileText,       recordLabel: "Upload Document" },
];

const PDF_PATHS = {
  onboarding: (id) => `/api/onboarding/${id}/pdf`,
  service_request: (id) => `/api/service-requests/${id}/pdf`,
  contract: (id) => `/api/contracts/${id}/pdf`,
  invoice: (id) => `/api/invoices/${id}/pdf`,
};

const FULL_EDITOR_PATH = {
  onboarding: (id) => `/app/onboarding?id=${id}`,
  service_request: (id) => `/app/service-requests?id=${id}`,
  contract: (id) => `/app/contracts?id=${id}`,
  invoice: (id) => `/app/invoices?id=${id}`,
  form: (id) => `/app/forms?id=${id}`,
  document: (id) => `/app/documents?id=${id}`,
};

export default function FormsDocuments() {
  const { currentOrgId } = useAuth();
  const [tab, setTab] = useState("onboarding");
  const [clients, setClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState(null);
  const [clientQuery, setClientQuery] = useState("");
  const [rows, setRows] = useState([]);
  const [loadingRows, setLoadingRows] = useState(false);
  const [selection, setSelection] = useState(null); // { id, mode: 'new'|'edit'|'view', templateId? }
  const [ctx, setCtx] = useState(null); // { client, status, editing, actions, completion }
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const mq = window.matchMedia("(max-width: 1023px)");
    const upd = () => setIsMobile(mq.matches);
    upd(); mq.addEventListener?.("change", upd);
    return () => mq.removeEventListener?.("change", upd);
  }, []);

  // Load clients once
  useEffect(() => {
    if (!currentOrgId) return;
    supabase.from("clients")
      .select("id,first_name,last_name,email,phone,referral_source")
      .eq("organization_id", currentOrgId).order("first_name")
      .then((r) => setClients(r.data || []));
  }, [currentOrgId]);

  const filteredClients = useMemo(() => {
    const q = clientQuery.trim().toLowerCase();
    if (!q) return clients;
    return clients.filter((c) => `${c.first_name} ${c.last_name} ${c.email || ""}`.toLowerCase().includes(q));
  }, [clients, clientQuery]);

  // Load records list for this tab
  const loadRows = async () => {
    if (!currentOrgId) return;
    setLoadingRows(true);
    // Reset rows to prevent cross-tab record leakage.
    setRows(tab === "form" ? { templates: [], completed: [] } : []);
    try {
      if (tab === "form") {
        // Available templates: filter out active ones from "completed" section per user spec.
        // Completed = form_responses.
        const [tpl, res] = await Promise.all([
          supabase.from("form_templates")
            .select("id,name,slug,status,updated_at,created_at")
            .eq("organization_id", currentOrgId)
            .neq("status", "archived")
            .order("updated_at", { ascending: false }),
          supabase.from("form_responses")
            .select("id,submitted_at,client_id,form_template_id,form_templates(name,status),clients(first_name,last_name)")
            .order("submitted_at", { ascending: false }).limit(50),
        ]);
        setRows({
          templates: tpl.data || [],
          completed: (res.data || []).filter((r) => {
            if (!selectedClient) return true;
            return r.client_id === selectedClient.id;
          }),
        });
      } else if (tab === "document") {
        const q = supabase.from("documents")
          .select("id,name,client_id,updated_at,created_at,clients(first_name,last_name)")
          .eq("organization_id", currentOrgId)
          .order("updated_at", { ascending: false });
        if (selectedClient) q.eq("client_id", selectedClient.id);
        const r = await q;
        setRows(r.data || []);
      } else {
        const params = new URLSearchParams({ organization_id: currentOrgId, kind: tab });
        if (selectedClient) params.set("client_id", selectedClient.id);
        const r = await apiFetch(`/forms-documents?${params.toString()}`);
        // Only show "completed" records (exclude active/draft template-like state)
        const items = (r.documents || []).filter((x) => x.status && x.status !== "active");
        setRows(items);
      }
    } catch (e) { toast.error(e.message || "Load failed"); }
    finally { setLoadingRows(false); }
  };
  useEffect(() => { loadRows(); /* eslint-disable-next-line */ }, [tab, currentOrgId, selectedClient?.id]);

  const startNew = () => {
    if (tab !== "form" && tab !== "document" && !selectedClient) return toast.error("Pick a client first");
    setSelection({ id: null, mode: "new" });
    setCtx(null);
  };

  const openRecord = (row) => {
    if (tab === "form") {
      // Forms tab has 3 kinds of rows:
      //   • row.templateId (fill an active template for a client) → fill mode
      //   • row.editTemplateId (edit an existing template)         → edit builder mode
      //   • row.id (a form response)                                → view mode
      if (row.editTemplateId) setSelection({ id: row.editTemplateId, mode: "edit" });
      else if (row.templateId) setSelection({ id: null, templateId: row.templateId, mode: "fill" });
      else setSelection({ id: row.id, mode: "view" });
      if (row.client_id) {
        const c = clients.find((x) => x.id === row.client_id);
        if (c) setSelectedClient(c);
      }
    } else {
      setSelection({ id: row.id, mode: "edit" });
      if (row.client_id && !selectedClient) {
        const c = clients.find((x) => x.id === row.client_id);
        if (c) setSelectedClient(c);
      }
    }
    setCtx(null);
  };

  const onSaved = () => { loadRows(); };
  const closeWorkspace = () => { setSelection(null); setCtx(null); };

  const downloadPDF = async (kind, id) => {
    const path = PDF_PATHS[kind]?.(id);
    if (!path) return toast.error("PDF not available");
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}${path}`, { headers: { Authorization: `Bearer ${session.access_token}` } });
      if (!res.ok) throw new Error(await res.text());
      const blob = await res.blob();
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob); link.download = `${kind}.pdf`;
      document.body.appendChild(link); link.click(); link.remove();
    } catch (e) { toast.error("Download failed"); }
  };

  const activeTab = TABS.find((t) => t.id === tab);

  // Renders the actual inline workspace panel based on the tab + selection.
  const renderWorkspacePanel = () => {
    if (!selection) return null;
    const shared = { client: selectedClient, recordId: selection.id, onSaved: (s) => {
      onSaved(s);
      if (s?.deleted) { closeWorkspace(); return; }
      if (s?.id && !selection.id) setSelection({ ...selection, id: s.id, mode: "edit" });
    }, onContext: setCtx };
    switch (tab) {
      case "onboarding":       return <OnboardingPanel {...shared} />;
      case "service_request":  return <ServiceRequestPanel {...shared} />;
      case "contract":         return <ContractPanel {...shared} />;
      case "invoice":          return <InvoicePanel {...shared} />;
      case "form":             return <FormPanel {...shared} templateId={selection.templateId} mode={selection.mode === "new" ? "new" : selection.mode} />;
      case "document":         return <DocumentPanel {...shared} />;
      default: return null;
    }
  };

  return (
    <div className="space-y-4" data-testid="forms-docs-page">
      <PageHeader
        title="Forms & Documents"
        subtitle="Your workspace for onboarding, requests, contracts, invoices, forms & files."
      />

      {/* Persistent category tabs */}
      <div className="flex gap-1 bg-white border border-[#E8E2D9] rounded-full p-1 overflow-x-auto">
        {TABS.map(({ id, label, icon: Icon }) => (
          <button
            key={id}
            data-testid={`tab-${id}`}
            onClick={() => { setTab(id); setSelection(null); setCtx(null); }}
            className={`px-4 py-2 rounded-full text-xs font-medium flex items-center gap-1.5 whitespace-nowrap ${tab === id ? "bg-[#E8712C] text-white" : "text-[#57534E] hover:bg-[#FAF8F5]"}`}
          >
            <Icon size={13} strokeWidth={1.75} />
            {label}
          </button>
        ))}
      </div>

      {/* Mobile-only workspace overlay (only mounts on small screens to avoid duplicate testids). */}
      {isMobile && selection && (
        <div className="fixed inset-0 z-40 bg-[#FAF8F5] overflow-y-auto" data-testid="mobile-workspace">
          <div className="p-4 border-b border-[#E8E2D9] bg-white flex items-center justify-between sticky top-0 z-10">
            <button onClick={closeWorkspace} className="text-sm flex items-center gap-1 text-[#57534E]" data-testid="mobile-back"><ArrowLeft size={14} /> Back</button>
            <div className="text-xs uppercase tracking-widest text-[#57534E]">{activeTab.label}</div>
            <div className="w-12" />
          </div>
          <div className="p-4">{renderWorkspacePanel()}</div>
          {ctx && (ctx.actions || []).length > 0 && (
            <div className="p-3 border-t border-[#E8E2D9] bg-white flex gap-2 flex-wrap sticky bottom-0">
              {ctx.actions.map((a) => (
                <Button key={a.key} data-testid={a.testid} disabled={a.disabled} onClick={a.onClick}
                  variant={a.variant === "primary" ? "default" : "outline"}
                  className={`rounded-full flex-1 min-w-[110px] ${a.variant === "primary" ? "bg-[#E8712C] hover:bg-[#C55A1F]" : ""}`}>
                  {a.icon && <a.icon size={13} className="mr-1.5" />}
                  {a.label}
                </Button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Workspace grid: full-width records LIST. Selecting one opens a Sheet drawer. */}
      <div className="grid grid-cols-12 gap-4">
        {/* LEFT — Category header + Start New + Records list (now full width) */}
        <aside className="col-span-12 space-y-3">
          <div className="bg-white border border-[#E8E2D9] rounded-2xl p-4 space-y-3">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div className="text-[10px] font-semibold uppercase tracking-widest text-[#57534E]">Client</div>
              <Button data-testid="fd-start-new" onClick={startNew} disabled={tab !== "form" && tab !== "document" && !selectedClient}
                className="rounded-full bg-[#E8712C] hover:bg-[#C55A1F] h-8 text-xs">
                <Plus size={12} className="mr-1" /> {activeTab.recordLabel}
              </Button>
            </div>
            <div className="relative">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#A8A29E]" />
              <Input placeholder={selectedClient ? `${selectedClient.first_name} ${selectedClient.last_name}` : "Search clients…"}
                value={clientQuery} onChange={(e) => setClientQuery(e.target.value)}
                className="pl-8" data-testid="fd-client-search" />
            </div>
            {clientQuery && (
              <div className="max-h-60 overflow-y-auto border border-[#E8E2D9] rounded-xl divide-y divide-[#E8E2D9]">
                {filteredClients.slice(0, 30).map((c) => (
                  <button key={c.id} onClick={() => { setSelectedClient(c); setClientQuery(""); }}
                    data-testid={`fd-client-${c.id}`}
                    className="w-full text-left px-3 py-2 hover:bg-[#FAF8F5] text-sm">
                    {c.first_name} {c.last_name}
                    {c.email && <span className="text-[#A8A29E] ml-2 text-xs">{c.email}</span>}
                  </button>
                ))}
              </div>
            )}
            {selectedClient && !clientQuery && (
              <button onClick={() => setSelectedClient(null)} className="text-[10px] text-[#B85C1F] hover:underline" data-testid="fd-clear-client">Clear client filter</button>
            )}
          </div>

          {tab === "form" && rows.templates?.length > 0 && (
            <div className="bg-white border border-[#E8E2D9] rounded-2xl p-4">
              <div className="text-[10px] font-semibold uppercase tracking-widest text-[#57534E] mb-2">Available Templates</div>
              <div className="divide-y divide-[#E8E2D9]">
                {rows.templates.map((t) => (
                  <div key={t.id} className="py-2 flex items-center justify-between gap-2 group">
                    <button onClick={() => selectedClient ? openRecord({ templateId: t.id, client_id: selectedClient.id }) : toast.error("Pick a client first")}
                      data-testid={`fd-template-${t.id}`} className="flex-1 text-left text-sm hover:text-[#B85C1F] truncate">{t.name}</button>
                    <button onClick={() => openRecord({ editTemplateId: t.id })}
                      data-testid={`fd-edit-tpl-${t.id}`}
                      className="text-[10px] text-[#E8712C] opacity-0 group-hover:opacity-100 hover:underline flex-shrink-0"
                      title="Edit template">edit</button>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="bg-white border border-[#E8E2D9] rounded-2xl overflow-hidden">
            <div className="px-4 py-2 border-b border-[#E8E2D9] flex items-center justify-between">
              <div className="text-[10px] font-semibold uppercase tracking-widest text-[#57534E]">
                {tab === "form" ? "Submitted Records" : tab === "document" ? "Documents" : "Records"}
              </div>
              {selectedClient && <span className="text-[10px] text-[#A8A29E]">for {selectedClient.first_name}</span>}
            </div>
            {loadingRows ? (
              <div className="p-6 text-center text-xs text-[#57534E]">Loading…</div>
            ) : (
              <RecordsList tab={tab} rows={rows} onOpen={openRecord} selectedId={selection?.id} clients={clients} />
            )}
          </div>
        </aside>
      </div>

      {/* Detail drawer — hosts the existing Panel components (Onboarding/ServiceRequest/Contract/Invoice/Form/Document) */}
      <Sheet open={!!selection} onOpenChange={(v) => { if (!v) closeWorkspace(); }}>
        <SheetContent side="right" className="w-full sm:max-w-2xl overflow-y-auto bg-white" data-testid="fd-drawer">
          <div className="flex items-center justify-between mb-4 pb-3 border-b border-[#E8E2D9] pr-8">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-[#A8A29E]">{activeTab.label}</div>
              <div className="font-display text-lg tracking-tight" data-testid="fd-drawer-title">
                {selection?.mode === "new" ? `New ${activeTab.label.slice(0, -1)}` :
                 selection?.mode === "edit" ? "Edit record" :
                 selection?.mode === "fill" ? "Fill form" : "Record details"}
                {ctx?.client && <span className="text-sm text-[#57534E] ml-2">· {ctx.client.first_name} {ctx.client.last_name}</span>}
              </div>
            </div>
          </div>
          {selection && (
            <div className="space-y-4">
              {renderWorkspacePanel()}
              {(ctx?.actions?.length > 0 || (selection?.id && FULL_EDITOR_PATH[tab]) || (selection?.id && PDF_PATHS[tab])) && (
                <div className="pt-4 border-t border-[#E8E2D9] flex flex-wrap gap-2 sticky bottom-0 bg-white pb-2" data-testid="fd-drawer-actions">
                  {(ctx?.actions || []).map((a) => (
                    <Button key={a.key} data-testid={a.testid} disabled={a.disabled} onClick={a.onClick}
                      variant={a.variant === "primary" ? "default" : "outline"}
                      className={`rounded-full ${a.variant === "primary" ? "bg-[#E8712C] hover:bg-[#C55A1F]" : ""}`}>
                      {a.icon && <a.icon size={13} className="mr-1.5" />} {a.label}
                    </Button>
                  ))}
                  {selection?.id && PDF_PATHS[tab] && (
                    <Button variant="outline" data-testid="fd-drawer-pdf" onClick={() => downloadPDF(tab, selection.id)} className="rounded-full">
                      <Download size={13} className="mr-1.5" /> Download PDF
                    </Button>
                  )}
                  {selection?.id && FULL_EDITOR_PATH[tab] && (
                    <Button variant="outline" data-testid="fd-drawer-full" onClick={() => window.open(FULL_EDITOR_PATH[tab](selection.id), "_blank")} className="rounded-full">
                      <ExternalLink size={13} className="mr-1.5" /> Full editor
                    </Button>
                  )}
                  <Button variant="ghost" data-testid="fd-drawer-close" onClick={closeWorkspace} className="rounded-full ml-auto">
                    <X size={13} className="mr-1" /> Close
                  </Button>
                </div>
              )}
            </div>
          )}
        </SheetContent>
      </Sheet>

    </div>
  );
}

/** Left-side records list per tab. */
function RecordsList({ tab, rows, onOpen, selectedId, clients }) {
  const clientName = (id) => {
    const c = clients.find((x) => x.id === id);
    return c ? `${c.first_name} ${c.last_name}` : "—";
  };

  if (tab === "form") {
    const list = rows?.completed || [];
    if (list.length === 0) return <div className="p-4 text-center text-xs text-[#A8A29E]">No completed responses yet.</div>;
    return (
      <div className="max-h-[calc(100vh-460px)] overflow-y-auto divide-y divide-[#E8E2D9]">
        {list.map((r) => (
          <button key={r.id} onClick={() => onOpen(r)}
            data-testid={`fd-record-${r.id}`}
            className={`w-full text-left px-4 py-3 hover:bg-[#FAF8F5] ${selectedId === r.id ? "bg-[#FDE9D8]" : ""}`}>
            <div className="text-sm font-medium truncate">{r.form_templates?.name || "Form"}</div>
            <div className="text-xs text-[#57534E] mt-0.5">
              {r.clients ? `${r.clients.first_name} ${r.clients.last_name}` : "—"} · {r.submitted_at ? formatDate(r.submitted_at) : "—"}
            </div>
          </button>
        ))}
      </div>
    );
  }

  const list = Array.isArray(rows) ? rows : [];
  if (list.length === 0) return <div className="p-4 text-center text-xs text-[#A8A29E]">No completed records yet.</div>;

  return (
    <div className="max-h-[calc(100vh-460px)] overflow-y-auto divide-y divide-[#E8E2D9]">
      {list.map((r) => (
        <button key={r.id} onClick={() => onOpen(r)}
          data-testid={`fd-record-${r.id}`}
          className={`w-full text-left px-4 py-3 hover:bg-[#FAF8F5] ${selectedId === r.id ? "bg-[#FDE9D8]" : ""}`}>
          <div className="flex items-center gap-2 mb-0.5">
            {r.number && <span className="text-[10px] font-medium text-[#B85C1F]">{r.number}</span>}
            {r.status && <StatusBadge value={r.status} />}
          </div>
          <div className="text-sm font-medium truncate">{r.title || r.name || "—"}</div>
          <div className="text-xs text-[#57534E] mt-0.5 truncate">
            {r.client_name || (r.client_id ? clientName(r.client_id) : "—")}
            {r.updated_at && <> · {formatDate(r.updated_at)}</>}
          </div>
        </button>
      ))}
    </div>
  );
}

function EmptyWorkspace({ activeTab, onStartNew, disabled }) {
  const Icon = activeTab.icon;
  return (
    <div className="bg-white border border-dashed border-[#E8E2D9] rounded-2xl p-16 text-center min-h-[420px] flex flex-col items-center justify-center" data-testid="workspace-empty">
      <div className="w-14 h-14 rounded-2xl bg-[#FDE9D8] text-[#E8712C] flex items-center justify-center mb-4">
        <Icon size={22} strokeWidth={1.5} />
      </div>
      <h3 className="font-display font-semibold text-lg tracking-tight">{activeTab.label} workspace</h3>
      <p className="text-sm text-[#57534E] mt-1 max-w-md">
        {disabled ? "Pick a client on the left to get started. " : ""}
        Selecting a record from the list on the left opens it here, or click below to start a new one.
      </p>
      <Button onClick={onStartNew} disabled={disabled} data-testid="workspace-empty-start"
        className="rounded-full bg-[#E8712C] hover:bg-[#C55A1F] mt-4">
        <Plus size={13} className="mr-1.5" /> {activeTab.recordLabel}
      </Button>
    </div>
  );
}

function ContextPanel({ tab, ctx, selection, onOpenFull, onDownloadPdf, onClose }) {
  if (!selection || !ctx) {
    return (
      <div className="bg-white border border-[#E8E2D9] rounded-2xl p-5 text-xs text-[#A8A29E]">
        Selecting or starting a record will show its details and actions here.
      </div>
    );
  }
  const { client, status, editing, completion, actions = [] } = ctx;
  const hasPdf = editing?.id && PDF_PATHS[tab];
  return (
    <div className="space-y-3">
      <div className="bg-white border border-[#E8E2D9] rounded-2xl p-4 space-y-2">
        <div className="text-[10px] font-semibold uppercase tracking-widest text-[#57534E]">Record Details</div>
        {client && (
          <div>
            <div className="text-[10px] text-[#A8A29E] uppercase tracking-widest">Client</div>
            <div className="text-sm font-medium">{client.first_name} {client.last_name}</div>
            {client.email && <div className="text-xs text-[#57534E]">{client.email}</div>}
          </div>
        )}
        {status && (
          <div>
            <div className="text-[10px] text-[#A8A29E] uppercase tracking-widest">Status</div>
            <StatusBadge value={status} />
          </div>
        )}
        {typeof completion === "number" && (
          <div>
            <div className="text-[10px] text-[#A8A29E] uppercase tracking-widest">Completion</div>
            <div className="text-sm font-medium">{completion}%</div>
          </div>
        )}
        {editing?.updated_at && (
          <div>
            <div className="text-[10px] text-[#A8A29E] uppercase tracking-widest">Last updated</div>
            <div className="text-xs text-[#57534E]">{formatDate(editing.updated_at)}</div>
          </div>
        )}
      </div>

      {actions.length > 0 && (
        <div className="bg-white border border-[#E8E2D9] rounded-2xl p-4 space-y-2">
          <div className="text-[10px] font-semibold uppercase tracking-widest text-[#57534E]">Actions</div>
          {actions.map((a) => (
            <Button key={a.key} data-testid={a.testid} disabled={a.disabled} onClick={a.onClick}
              variant={a.variant === "primary" ? "default" : "outline"}
              className={`w-full rounded-full ${a.variant === "primary" ? "bg-[#E8712C] hover:bg-[#C55A1F]" : ""}`}>
              {a.icon && <a.icon size={13} className="mr-1.5" />}
              {a.label}
            </Button>
          ))}
          {hasPdf && (
            <Button variant="outline" className="w-full rounded-full" data-testid="ctx-pdf" onClick={onDownloadPdf}>
              <Download size={13} className="mr-1.5" /> Download PDF
            </Button>
          )}
          {editing?.id && FULL_EDITOR_PATH[tab] && (
            <Button variant="outline" className="w-full rounded-full text-[10px]" data-testid="ctx-open-full" onClick={onOpenFull}>
              <ExternalLink size={11} className="mr-1.5" /> Open in full editor
            </Button>
          )}
          <Button variant="outline" className="w-full rounded-full text-[#57534E]" data-testid="ctx-close" onClick={onClose}>
            Close record
          </Button>
        </div>
      )}
    </div>
  );
}
